import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { logoutApi } from '../api/logout.api';
import { getStoredRefreshToken } from '../../login/api/loginApi';

/**
 * Clears all token keys from both localStorage and sessionStorage.
 * Role-agnostic — applies identically to admin, manager, and user1.
 */
function clearTokenStorage(): void {
  const keys = ['access_token', 'refresh_token', 'refresh_expires_in', 'access_expires_in'];
  keys.forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

/**
 * Mutation: POST /api/eraf/auth/logout with revokeAll=false
 *
 * R7-002 fix: clearTokenStorage is unconditionally called for all roles.
 * R7-003 fix: mutateRef captures the latest mutate function reference to
 *   prevent stale closure when the component re-renders between calls.
 */
export function useLogout() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      const refreshToken = getStoredRefreshToken() ?? '';
      return logoutApi.logout({ refreshToken, revokeAll: false });
    },
    onSuccess: () => {
      // R7-002: clear storage first, then navigate — same path for every role
      clearTokenStorage();
      qc.clear();
      navigate('/login', { replace: true });
    },
    onError: () => {
      // R7-002: also clear local tokens on API error so stale tokens don't linger
      clearTokenStorage();
      qc.clear();
      // E2E fix: always navigate to /login even on error (tokens already cleared)
      navigate('/login', { replace: true });
    },
  });

  // R7-003: stable ref that always points to latest mutate
  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;

  return { ...mutation, mutateRef };
}
